import React, { useEffect, useState, useCallback, useRef, FunctionComponent } from 'react';
import { Presentation } from './presentation';
import type { CompanyDetailProps as Props, CompanyDetailState } from './types';
import type { IdCountKey } from '../../../types/companyDetail';
import { useDevice } from '../../../hooks/device';

export const Component: FunctionComponent<Props> = (props) => {
  const isPc = useDevice();
  const employmentRef = useRef<HTMLElement>(null);
  const isRecruitmentPopupEnabled = Boolean(
    props.recruitmentPopupVariant &&
    props.hasNoReleasedRecruitments &&
    props.user === 'without_login' &&
    props.isPage === 'show'
  );
  const [state, setState] = useState<CompanyDetailState>({
    recruitmentIds: 0,
    eventIds: 0,
    postIds: 0,
    memberIds: 0,
    brandIds: 0,
    dataCategory: 'companycontent',
    dataValueCompanyPageHeading: {
      selfNavigation: 'self_navigation',
      selfNavigationHome: 'self_navigation_home',
      selfNavigationRecruitments: 'self_navigation_recruitments',
      selfNavigationEvents: 'self_navigation_events',
      selfNavigationStories: 'self_navigation_stories',
    },
    dataValueCompanyPageRecruitments: {
      moreSelfRecruitments: 'more_self_recruitments',
      selfRecruitments: 'self_recruitments',
    },
    dataValueCompanyPageEmployments: 'employment',
    dataValueCompanyPageBrands: 'self_brands',
    dataValueCompanyPageEvents: {
      moreSelfEvent: 'more_self_event',
      selfEvent: 'self_event',
    },
    dataValueCompanyPageStories: {
      moreSelfStories: 'more_self_stories',
      selfStories: 'self_stories',
    },
    dataValueCompanyPageTvs: {
      moreSelfMovie: 'more_self_movie',
      selfMovie: 'self_movie',
    },
    dataValueCompanyPageAbout: {
      industryLink: 'industry_link',
      companyLink: 'company_link',
    },
    dataValueCompanyPagePeople: 'company_people_link',
    isReachedTarget: false,
    isPassed5Seconds: false,
    isModalOpen: false,
  });

  const setIdCount = useCallback((name: IdCountKey, value: number) => {
    switch (name) {
      case 'recruitmentIds':
        setState(prev => ({ ...prev, recruitmentIds: value }));
        break;
      case 'eventIds':
        setState(prev => ({ ...prev, eventIds: value }));
        break;
      case 'postIds':
        setState(prev => ({ ...prev, postIds: value }));
        break;
      case 'memberIds':
        setState(prev => ({ ...prev, memberIds: value }));
        break;
      case 'brandIds':
        setState(prev => ({ ...prev, brandIds: value }));
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    if (!isRecruitmentPopupEnabled) return;

    const employmentElement = employmentRef.current;
    if (!employmentElement) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;

      setState(prev => ({ ...prev, isReachedTarget: true }));
      observer.disconnect();
    });

    observer.observe(employmentElement);
    return () => observer.disconnect();
  }, [isRecruitmentPopupEnabled]);

  useEffect(() => {
    if (!isRecruitmentPopupEnabled) return;

    const timer = setTimeout(() => {
      setState(prev => ({ ...prev, isPassed5Seconds: true }));
    }, 5000);
    return () => clearTimeout(timer);
  }, [isRecruitmentPopupEnabled]);

  // isPassed5SecondsとisReachedTargetがどちらもtrueになったらmodalを開くstateをtrueにする
  useEffect(() => {
    if (isRecruitmentPopupEnabled && state.isPassed5Seconds && state.isReachedTarget) {
      setState(prev => ({ ...prev, isModalOpen: true }));
    }
  }, [isRecruitmentPopupEnabled, state.isPassed5Seconds, state.isReachedTarget]);

  const handleModalClose = useCallback(() => {
    setState(prev => ({ ...prev, isModalOpen: false }));
  }, []);

  return (
    <Presentation
      {...props}
      isPc={isPc.isPc}
      state={state}
      setIdCount={setIdCount}
      employmentRef={employmentRef}
      handleModalClose={handleModalClose}
    />
  );
};
